/**
 * Waiting on the persisted auth store without being able to wait forever.
 *
 * zustand's persist middleware flips `hasHydrated` and notifies its finish
 * listeners only on the success path. A rejected storage read, or a JSON parse
 * error on a truncated `auth-storage` value, leaves `hasHydrated` false and
 * notifies nobody. The native app shells gate their entire UI on that signal,
 * so one unreadable value stranded launch on an empty screen that relaunching
 * could not clear (the value is still unreadable next time).
 *
 * Waiters here always settle. Callers treat the failsafe like any other
 * "hydration finished with no stored session": the API layer still refreshes
 * on demand, so a real session recovers on the next request instead of the app
 * hanging before first paint.
 */

export const AUTH_HYDRATION_FAILSAFE_MS = 3000;

/**
 * Calls `onHydrated` once — as soon as hydration finishes, or at the failsafe.
 * Returns a cancel function; cancelling before it settles never calls back.
 */
export function subscribeAuthHydrated(persistApi, onHydrated, timers = globalThis) {
  if (!persistApi || typeof persistApi.hasHydrated !== 'function') {
    onHydrated();
    return () => {};
  }
  if (persistApi.hasHydrated()) {
    onHydrated();
    return () => {};
  }

  let settled = false;
  let unsubscribe = null;
  let failsafeId = null;

  const release = () => {
    if (failsafeId !== null) {
      timers.clearTimeout(failsafeId);
      failsafeId = null;
    }
    unsubscribe?.();
    unsubscribe = null;
  };

  const finish = () => {
    if (settled) return;
    settled = true;
    release();
    onHydrated();
  };

  failsafeId = timers.setTimeout(finish, AUTH_HYDRATION_FAILSAFE_MS);
  unsubscribe = persistApi.onFinishHydration?.(finish) ?? null;

  if (settled) {
    // `finish` already ran, so `release` could not see the subscription above.
    release();
  } else if (persistApi.hasHydrated()) {
    // Hydration can land between the guard at the top and the subscription,
    // and that notification is not replayed for late subscribers.
    finish();
  }

  return () => {
    if (settled) return;
    settled = true;
    release();
  };
}
